// ─── DashboardNavbar.tsx — Navigation bar extracted from Dashboard ────────────
//
// Main nav bar: logo, navigation links, language toggle, login/register/account dropdown
// Sub-bar: domain display, "New scan" button, "Download PDF" button
//
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, BookOpen, ShieldCheck, RotateCcw, FileDown } from 'lucide-react';
import WezeaLogo from './WezeaLogo';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import type { ScanStatus } from '../types/scanner';
import type { ScanResult } from '../types/scanner';

// ── Props ────────────────────────────────────────────────────────────────────

export interface DashboardNavbarProps {
  /** Current scanner status — controls sub-bar visibility */
  scannerStatus: ScanStatus;
  /** Current scan result — controls PDF download button visibility */
  scannerResult: ScanResult | null;

  /** Reset scan — logo click + "New scan" button */
  onReset: () => void;
  /** Open the email-capture / PDF download modal */
  onOpenPdfModal: () => void;
  /** Open pricing modal (called with analytics source) */
  onOpenPricing: (source: 'nav') => void;
  /** Open the change-password modal */
  onOpenPasswordModal: () => void;

  // Analytics-wrapped navigation helpers
  goLogin:    (source: 'nav') => void;
  goRegister: (source: 'nav') => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function DashboardNavbar({
  scannerStatus,
  scannerResult,
  onReset,
  onOpenPdfModal,
  onOpenPricing,
  onOpenPasswordModal,
  goLogin,
  goRegister,
}: DashboardNavbarProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang, setLang } = useLanguage();

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isIdle     = scannerStatus === 'idle';
  const isScanning = scannerStatus === 'scanning';
  const isSuccess  = scannerStatus === 'success';

  return (
    <nav
      className="sticky top-0 z-20 backdrop-blur-md"
      style={{
        background: 'rgba(255,255,255,0.85)',
        borderBottom: '1px solid var(--color-border)',
        boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
      }}
    >
      {/* ── Main bar ── */}
      <div className="max-w-6xl mx-auto px-4 h-[56px] flex items-center gap-4">

        {/* LEFT — Logo (cliquable → retour accueil) */}
        <button
          onClick={onReset}
          className="shrink-0 group"
          aria-label="Retour à l'accueil"
        >
          <WezeaLogo size="md" showSub className="group-hover:opacity-80 transition-opacity" />
        </button>

        {/* CENTER — Navigation principale */}
        <div className="hidden md:flex items-center gap-1 justify-start ml-6">

          {/* CTA upgrade — Free connecté uniquement */}
          {user && user.plan === 'free' && (
            <button
              onClick={() => onOpenPricing('nav')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[var(--color-accent-strong)] bg-[var(--color-accent-soft)] border border-[rgba(13,148,136,0.25)] hover:bg-[rgba(13,148,136,0.14)] transition-all"
            >
              <Zap size={11} />
              {lang === 'fr' ? 'Passer Starter' : 'Upgrade'}
            </button>
          )}

          {/* Blog */}
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-[var(--color-body)] hover:text-[var(--color-ink)] hover:bg-[var(--color-inset)] transition-all"
          >
            <BookOpen size={12} />
            Blog
          </button>

          {/* Conformité NIS2 — lien vers la landing page */}
          <button
            onClick={() => navigate('/conformite-nis2')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-inset)] transition-all"
          >
            <ShieldCheck size={12} />
            {lang === 'fr' ? 'NIS2 & RGPD' : 'NIS2 & GDPR'}
          </button>

        </div>

        {/* RIGHT — Lang + compte */}
        <div className="flex items-center gap-2.5 shrink-0 ml-auto">

          {/* Lang toggle */}
          <div className="flex overflow-hidden rounded-full bg-[var(--color-inset)] border border-[var(--color-border)] p-0.5">
            <button
              onClick={() => setLang('fr')}
              className="px-2.5 py-1 text-xs font-mono font-bold rounded-full transition-all"
              style={lang === 'fr'
                ? { color: '#fff', background: 'var(--color-accent)' }
                : { color: 'var(--color-muted)' }
              }
            >FR</button>
            <button
              onClick={() => setLang('en')}
              className="px-2.5 py-1 text-xs font-mono font-bold rounded-full transition-all"
              style={lang === 'en'
                ? { color: '#fff', background: 'var(--color-accent)' }
                : { color: 'var(--color-muted)' }
              }
            >EN</button>
          </div>

          {/* Séparateur */}
          <div className="w-px h-4 bg-[var(--color-border-strong)] shrink-0" />

          {/* Compte — visiteur */}
          {!user && (
            <>
              <button
                onClick={() => goLogin('nav')}
                className="px-3 py-1.5 rounded-full text-xs font-medium text-[var(--color-body)] hover:text-[var(--color-ink)] hover:bg-[var(--color-inset)] transition-all"
              >
                {lang === 'fr' ? 'Connexion' : 'Sign in'}
              </button>
              <button
                onClick={() => goRegister('nav')}
                className="sku-btn-primary px-4 py-1.5 text-xs hidden sm:block"
              >
                {lang === 'fr' ? 'Créer un compte' : 'Sign up'}
              </button>
            </>
          )}

          {/* Compte — connecté : avatar + dropdown */}
          {user && (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-inset)] hover:border-[var(--color-border-strong)] transition-all"
              >
                <div
                  className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-black shrink-0"
                  style={
                    user.plan === 'dev'
                      ? { background: 'rgba(147,51,234,.12)', border: '1px solid rgba(147,51,234,.3)', color: '#7e22ce' }
                      : { background: 'var(--color-accent-soft)', border: '1px solid rgba(13,148,136,.3)', color: 'var(--color-accent-strong)' }
                  }
                >
                  {(user.first_name ?? user.email)[0].toUpperCase()}
                </div>
                <div className="hidden sm:flex items-center gap-1.5">
                  <span className="text-[11.5px] font-semibold text-[var(--color-ink)] leading-none max-w-[80px] truncate">
                    {user.first_name ?? user.email.split('@')[0]}
                  </span>
                  <span
                    className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded-full shrink-0"
                    style={
                      user.plan === 'dev'
                        ? { color: '#7e22ce', background: 'rgba(147,51,234,.1)', border: '1px solid rgba(147,51,234,.22)' }
                        : user.plan === 'pro' || user.plan === 'starter'
                        ? { color: 'var(--color-accent-strong)', background: 'var(--color-accent-soft)', border: '1px solid rgba(13,148,136,.22)' }
                        : { color: 'var(--color-muted)', background: 'var(--color-inset)', border: '1px solid var(--color-border)' }
                    }
                  >
                    {user.plan === 'dev' ? 'BUSINESS' : user.plan === 'pro' ? 'PRO' : user.plan === 'starter' ? 'STARTER' : (lang === 'fr' ? 'GRATUIT' : 'FREE')}
                  </span>
                </div>
                <svg width="10" height="10" fill="none" stroke="var(--color-faint)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" className="ml-0.5"><polyline points="6 9 12 15 18 9"/></svg>
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-52 rounded-2xl z-50 overflow-hidden bg-[var(--color-surface)] border border-[var(--color-border)]"
                  style={{ boxShadow: 'var(--shadow-pop)' }}
                >
                  {/* Header */}
                  <div className="px-3 pt-3 pb-2.5 border-b border-[var(--color-border)]">
                    <p className="text-[11px] text-[var(--color-muted)] font-mono truncate">{user.email}</p>
                  </div>

                  {/* Section Navigation */}
                  <div className="pt-1.5 pb-1">
                    <p className="px-3 pb-1 text-[9px] font-mono text-[var(--color-faint)] uppercase tracking-widest">{lang === 'fr' ? 'Navigation' : 'Navigate'}</p>
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/history'); }}
                      className="w-full text-left px-3 py-2 text-xs text-[var(--color-body)] hover:bg-[var(--color-inset)] hover:text-[var(--color-ink)] transition flex items-center gap-2.5"
                    >
                      <span className="w-[18px] h-[18px] rounded-[6px] shrink-0 inline-flex items-center justify-center bg-[var(--color-accent-soft)] border border-[rgba(13,148,136,.22)]">
                        <svg width="10" height="10" fill="none" stroke="var(--color-accent-strong)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="12 8 12 12 14 14"/><circle cx="12" cy="12" r="9"/></svg>
                      </span>
                      {lang === 'fr' ? 'Historique' : 'History'}
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/espace-client'); }}
                      className="w-full text-left px-3 py-2 text-xs text-[var(--color-body)] hover:bg-[var(--color-inset)] hover:text-[var(--color-ink)] transition flex items-center gap-2.5"
                    >
                      <span className="w-[18px] h-[18px] rounded-[6px] shrink-0 inline-flex items-center justify-center bg-[var(--color-accent-soft)] border border-[rgba(13,148,136,.22)]">
                        <svg width="10" height="10" fill="none" stroke="var(--color-accent-strong)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      </span>
                      {lang === 'fr' ? 'Mon espace' : 'My space'}
                    </button>
                    {!user.google_id && (
                      <button
                        onClick={() => { setUserMenuOpen(false); onOpenPasswordModal(); }}
                        className="w-full text-left px-3 py-2 text-xs text-[var(--color-body)] hover:bg-[var(--color-inset)] hover:text-[var(--color-ink)] transition flex items-center gap-2.5"
                      >
                        <span className="w-[18px] h-[18px] rounded-[6px] shrink-0 inline-flex items-center justify-center bg-[var(--color-accent-soft)] border border-[rgba(13,148,136,.22)]">
                          <svg width="10" height="10" fill="none" stroke="var(--color-accent-strong)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </span>
                        {lang === 'fr' ? 'Changer le mot de passe' : 'Change password'}
                      </button>
                    )}
                  </div>

                  {/* Section Aide */}
                  <div className="border-t border-[var(--color-border)] pt-1.5 pb-1">
                    <p className="px-3 pb-1 text-[9px] font-mono text-[var(--color-faint)] uppercase tracking-widest">{lang === 'fr' ? 'Aide' : 'Help'}</p>
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/contact'); }}
                      className="w-full text-left px-3 py-2 text-xs text-[var(--color-body)] hover:bg-[var(--color-inset)] hover:text-[var(--color-ink)] transition flex items-center gap-2.5"
                    >
                      <span className="w-[18px] h-[18px] rounded-[6px] shrink-0 inline-flex items-center justify-center bg-[rgba(99,102,241,.12)] border border-[rgba(99,102,241,.22)]">
                        <svg width="10" height="10" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      </span>
                      {lang === 'fr' ? 'Contacter le support' : 'Contact support'}
                    </button>
                    {user?.is_admin && (
                      <button
                        onClick={() => { setUserMenuOpen(false); navigate('/admin'); }}
                        className="w-full text-left px-3 py-2 text-xs text-[var(--color-body)] hover:bg-[var(--color-inset)] hover:text-[var(--color-ink)] transition flex items-center gap-2.5"
                      >
                        <span className="w-[18px] h-[18px] rounded-[6px] shrink-0 inline-flex items-center justify-center bg-[var(--color-inset)] border border-[var(--color-border)]">
                          <svg width="10" height="10" fill="none" stroke="var(--color-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                        </span>
                        Admin
                      </button>
                    )}
                  </div>

                  {/* Déconnexion */}
                  <div className="border-t border-[var(--color-border)] py-1">
                    <button
                      onClick={() => { setUserMenuOpen(false); logout(); }}
                      className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-600 transition flex items-center gap-2.5"
                    >
                      <span className="w-[18px] h-[18px] rounded-[6px] shrink-0 inline-flex items-center justify-center bg-red-50 border border-red-200">
                        <svg width="10" height="10" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      </span>
                      {lang === 'fr' ? 'Déconnexion' : 'Sign out'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Sous-barre contextuelle (actions scan) — visible après scan seulement ── */}
      {!isIdle && (
        <div className="border-t flex items-center gap-2 px-4 py-1.5 max-w-6xl mx-auto border-[var(--color-border)]">
          <span className="flex-1 text-[11px] text-[var(--color-muted)] font-mono truncate">
            {isSuccess && scannerResult
              ? scannerResult.domain
              : isScanning
              ? (lang === 'fr' ? 'Scan en cours…' : 'Scanning…')
              : (lang === 'fr' ? 'Erreur de scan' : 'Scan error')}
          </span>
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-[var(--color-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-inset)] border border-[var(--color-border)] transition-all"
          >
            <RotateCcw size={10} />
            {lang === 'fr' ? 'Nouveau scan' : 'New scan'}
          </button>
          {isSuccess && scannerResult && (
            <motion.button
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => onOpenPdfModal()}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold text-[var(--color-accent-strong)] bg-[var(--color-accent-soft)] border border-[rgba(13,148,136,0.25)] hover:bg-[rgba(13,148,136,0.14)] transition-all"
            >
              <FileDown size={10} />
              {lang === 'fr' ? 'Télécharger PDF' : 'Download PDF'}
            </motion.button>
          )}
        </div>
      )}
    </nav>
  );
}
